import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  ChevronDown,
  GripVertical,
  MessageSquare,
  Paperclip,
} from "lucide-react";
import type { ChamadoGestao } from "../../../types/portalCliente";
import {
  mensagemDeErro,
  useCapacidades,
  useLembrarSeveridadeAberta,
  useReordenarChamados,
  useSeveridadesAbertas,
} from "./api";
import {
  DIAS_PARADO,
  descricaoUtil,
  diasDeEspera,
  diasDesde,
  recortarGrupo,
  textoDeDias,
  valorDominante,
} from "./resumo";
import {
  SEVERIDADES_GESTAO,
  bordaSeveridade,
  formatarData,
  pontoSeveridade,
  rotuloSeveridade,
} from "./severidade";

type Colunas = Record<number, number[]>;

/** Ordem por severidade a partir da lista do servidor, que já vem ordenada. */
const montarColunas = (itens: ChamadoGestao[]): Colunas => {
  const colunas: Colunas = { 0: [], 1: [], 2: [], 3: [] };
  for (const c of itens) (colunas[c.severidade ?? 3] ?? colunas[3]).push(c.id);
  return colunas;
};

interface QuadroProps {
  /** O recorte a exibir: aba, segmento e período escolhidos. */
  itens: ChamadoGestao[];
  /**
   * A aba Novas inteira, sem segmento nem período. A gravação exige o conjunto
   * completo, porque a prioridade é a posição dentro do grupo: mandar só o que
   * está na tela embaralharia os escondidos.
   */
  todos?: ChamadoGestao[];
  podeReordenar?: boolean;
}

export const QuadroSeveridades = ({
  itens,
  todos,
  podeReordenar = false,
}: QuadroProps) => {
  const completa = todos ?? itens;

  const porId = useMemo(() => new Map(completa.map((c) => [c.id, c])), [completa]);
  const visiveis = useMemo(() => new Set(itens.map((c) => c.id)), [itens]);

  const [colunas, setColunas] = useState<Colunas>(() => montarColunas(completa));
  useEffect(() => setColunas(montarColunas(completa)), [completa]);

  const reordenar = useReordenarChamados();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);

  // Grupo aberto ou fechado: o que a pessoa mexeu nesta tela vale mais que o
  // gravado, e o gravado vale mais que o padrão, tudo aberto.
  const { data: capacidades } = useCapacidades();
  const { data: abertasGravadas } = useSeveridadesAbertas();
  const lembrar = useLembrarSeveridadeAberta();
  const [abertasNaTela, setAbertasNaTela] = useState<Record<number, boolean>>({});

  const estaAberto = (severidade: number): boolean =>
    abertasNaTela[severidade] ??
    abertasGravadas?.find((s) => s.severidade === severidade)?.aberta ??
    true;

  const alternar = (severidade: number) => {
    const aberta = !estaAberto(severidade);
    setAbertasNaTela((atual) => ({ ...atual, [severidade]: aberta }));
    // Sem a escrita ligada, a preferência vale só enquanto a tela estiver aberta.
    if (capacidades?.escritasHabilitadas) lembrar.mutate({ severidade, aberta });
  };
  const [erro, setErro] = useState<string | null>(null);

  const sensores = useSensors(
    // 5px de folga: sem isso, clicar no card viraria arraste e o link não abriria.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // O que se repete em quase toda linha é dito uma vez no cabeçalho.
  const statusComum = useMemo(
    () => valorDominante(itens.map((c) => c.statusChamado)),
    [itens]
  );
  const categoriaComum = useMemo(
    () => valorDominante(itens.map((c) => c.categoriaChamado)),
    [itens]
  );
  const nota = [statusComum, categoriaComum].filter(Boolean).join(" · ");

  const algumGrupoComVariosProjetos = useMemo(
    () =>
      SEVERIDADES_GESTAO.some(
        (sev) => new Set(itens.filter((c) => c.severidade === sev).map((c) => c.idProjeto)).size > 1
      ),
    [itens]
  );

  // A prioridade é um ranking por projeto (R21): arrastar para junto de um
  // chamado de outro projeto não tem o que gravar, então não mexe.
  const projetoDe = (id: string): number | null =>
    porId.get(Number(id))?.idProjeto ?? null;
  const ehGrupo = (id: string) => id.startsWith("sev-");

  const grupoDe = (id: string): number | null => {
    if (id.startsWith("sev-")) return Number(id.slice(4));
    const numero = Number(id);
    for (const sev of SEVERIDADES_GESTAO) {
      if (colunas[sev]?.includes(numero)) return sev;
    }
    return null;
  };

  const aoArrastarSobre = ({ active, over }: DragOverEvent) => {
    if (!over) return;
    const de = grupoDe(String(active.id));
    const para = grupoDe(String(over.id));
    if (de === null || para === null || de === para) return;
    if (!ehGrupo(String(over.id)) && projetoDe(String(over.id)) !== projetoDe(String(active.id))) return;

    setColunas((anterior) => {
      const id = Number(active.id);
      const proximo = { ...anterior, [de]: [...anterior[de]], [para]: [...anterior[para]] };
      proximo[de] = proximo[de].filter((x) => x !== id);
      const posicao = String(over.id).startsWith("sev-")
        ? proximo[para].length
        : Math.max(0, proximo[para].indexOf(Number(over.id)));
      proximo[para].splice(posicao, 0, id);
      return proximo;
    });
  };

  const aoSoltar = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const de = grupoDe(String(active.id));
    const para = grupoDe(String(over.id));
    if (de === null || para === null) return;

    let proximo = colunas;
    const mesmoProjeto =
      ehGrupo(String(over.id)) || projetoDe(String(over.id)) === projetoDe(String(active.id));
    if (de === para && mesmoProjeto) {
      const atual = colunas[de].indexOf(Number(active.id));
      const destino = String(over.id).startsWith("sev-")
        ? colunas[para].length - 1
        : colunas[para].indexOf(Number(over.id));
      if (atual !== destino && destino >= 0) {
        proximo = { ...colunas, [de]: arrayMove(colunas[de], atual, destino) };
        setColunas(proximo);
      }
    }

    // Sem mudança em relação ao servidor não há o que gravar.
    const doServidor = montarColunas(completa);
    if (JSON.stringify(proximo) === JSON.stringify(doServidor)) return;

    setErro(null);
    reordenar.mutate(
      SEVERIDADES_GESTAO.map((severidade) => ({
        severidade,
        chamadoIds: proximo[severidade] ?? [],
      })),
      {
        onSuccess: () => setSalvoEm(new Date()),
        onError: (e) => {
          // Nunca deixar a tela mostrando uma ordem que não foi gravada.
          setColunas(doServidor);
          setErro(mensagemDeErro(e, "Não foi possível salvar a nova ordem."));
        },
      }
    );
  };

  if (itens.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200/60 bg-white/70 p-12 text-center backdrop-blur-sm">
        <p className="font-medium text-gray-600">
          Nenhum chamado nesta combinação de filtros.
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Ajuste a situação, o tipo de demanda ou o período.
        </p>
      </div>
    );
  }

  const grupos = SEVERIDADES_GESTAO.map((severidade) => (
    <GrupoSeveridade
      key={severidade}
      severidade={severidade}
      ids={colunas[severidade] ?? []}
      porId={porId}
      visiveis={visiveis}
      aberto={estaAberto(severidade)}
      aoAlternar={() => alternar(severidade)}
      podeReordenar={podeReordenar}
      ocultarStatus={statusComum}
      ocultarCategoria={categoriaComum}
    />
  ));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-gray-500">
          {podeReordenar &&
            (algumGrupoComVariosProjetos
              ? "Arraste para priorizar; a ordem vale dentro de cada produto, como a equipe a vê"
              : "Arraste para priorizar por severidade")}
          {podeReordenar && nota && " · "}
          {nota && `${nota}, exceto onde indicado`}
        </p>
        <SituacaoDaGravacao
          gravando={reordenar.isPending}
          salvoEm={salvoEm}
          erro={erro}
        />
      </div>

      {podeReordenar ? (
        <DndContext
          sensors={sensores}
          collisionDetection={closestCorners}
          onDragOver={aoArrastarSobre}
          onDragEnd={aoSoltar}
        >
          {grupos}
        </DndContext>
      ) : (
        grupos
      )}
    </div>
  );
};

const SituacaoDaGravacao = ({
  gravando,
  salvoEm,
  erro,
}: {
  gravando: boolean;
  salvoEm: Date | null;
  erro: string | null;
}) => {
  if (erro) return <span className="text-red-600">{erro}</span>;
  if (gravando) return <span className="text-gray-500">Salvando ordem…</span>;
  if (salvoEm)
    return (
      <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
        <Check className="h-3.5 w-3.5" />
        Ordem salva às{" "}
        {salvoEm.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
      </span>
    );
  return null;
};

interface GrupoProps {
  severidade: number;
  /** Ordem completa do grupo, inclusive os escondidos pelo filtro. */
  ids: number[];
  porId: Map<number, ChamadoGestao>;
  visiveis: Set<number>;
  /** Lembrado por pessoa, e não só nesta tela. */
  aberto: boolean;
  aoAlternar: () => void;
  podeReordenar: boolean;
  ocultarStatus: string | null;
  ocultarCategoria: string | null;
}

const GrupoSeveridade = ({
  severidade,
  ids,
  porId,
  visiveis,
  aberto,
  aoAlternar,
  podeReordenar,
  ocultarStatus,
  ocultarCategoria,
}: GrupoProps) => {
  const [mostrarTudo, setMostrarTudo] = useState(false);

  // O grupo inteiro é alvo de soltura, para arrastar para um grupo vazio.
  const { setNodeRef, isOver } = useDroppable({ id: `sev-${severidade}` });

  const lista = useMemo(
    () =>
      ids
        .map((id) => porId.get(id))
        .filter((c): c is ChamadoGestao => Boolean(c) && visiveis.has(c!.id)),
    [ids, porId, visiveis]
  );

  const recorte = useMemo(() => recortarGrupo(lista), [lista]);
  const vazio = lista.length === 0;
  const truncado = !mostrarTudo && lista.length > recorte.mostrados.length;
  const exibidos = truncado ? recorte.mostrados : lista;
  const restantes = lista.length - exibidos.length;

  // Com o grupo recortado, arrastar gravaria uma ordem parcial.
  const arrastavel = podeReordenar && !truncado;

  // Mais de um projeto no grupo: um bloco por projeto, porque a ordem entre
  // projetos não existe no banco (R21) e não pode ser prometida na tela.
  const subgrupos = useMemo(() => {
    const porProjeto = new Map<number | null, ChamadoGestao[]>();
    for (const chamado of exibidos) {
      const chave = chamado.idProjeto ?? null;
      porProjeto.set(chave, [...(porProjeto.get(chave) ?? []), chamado]);
    }
    return [...porProjeto.entries()]
      .map(([idProjeto, chamados]) => ({
        idProjeto,
        nome: chamados[0]?.projetoNome ?? "Sem produto",
        chamados,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [exibidos]);

  const desenharCard = (chamado: ChamadoGestao) =>
    arrastavel ? (
      <CardArrastavel
        key={chamado.id}
        chamado={chamado}
        ocultarStatus={ocultarStatus}
        ocultarCategoria={ocultarCategoria}
      />
    ) : (
      <CardChamado
        key={chamado.id}
        chamado={chamado}
        ocultarStatus={ocultarStatus}
        ocultarCategoria={ocultarCategoria}
      />
    );

  const resumo = [
    recorte.aguardando > 0 ? `${recorte.aguardando} aguardando você` : null,
    recorte.parados > 0
      ? `${recorte.parados} ${recorte.parados === 1 ? "parado" : "parados"} há mais de ${DIAS_PARADO} dias`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      ref={setNodeRef}
      className={[
        "rounded-xl",
        vazio ? "opacity-60" : "",
        isOver && vazio ? "ring-1 ring-dashed ring-blue-400" : "",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => !vazio && aoAlternar()}
        disabled={vazio}
        aria-expanded={aberto && !vazio}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-2 text-left transition-colors enabled:hover:bg-gray-50"
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${pontoSeveridade(severidade)}`} />
        <span className="text-sm font-semibold text-gray-800">
          {rotuloSeveridade(severidade)}
        </span>
        <span className="rounded-full border border-gray-200 bg-white px-2 text-xs font-semibold tabular-nums text-gray-600">
          {lista.length}
        </span>
        {resumo && <span className="ml-auto text-xs text-gray-500">{resumo}</span>}
        {!vazio && (
          <ChevronDown
            className={[
              "h-4 w-4 shrink-0 text-gray-400 transition-transform",
              resumo ? "ml-2" : "ml-auto",
              aberto ? "rotate-180" : "",
            ].join(" ")}
          />
        )}
      </button>

      {aberto && !vazio && (
        <div className="space-y-2 pb-2">
          {subgrupos.length > 1
            ? subgrupos.map((subgrupo) => (
                <div key={subgrupo.idProjeto ?? "sem-projeto"} className="space-y-2">
                  <p className="flex items-center gap-2 px-1 pt-1 text-xs font-medium text-gray-500">
                    <span className="truncate">{subgrupo.nome}</span>
                    <span className="tabular-nums text-gray-400">{subgrupo.chamados.length}</span>
                  </p>
                  <SortableContext
                    items={subgrupo.chamados.map((c) => String(c.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    {subgrupo.chamados.map(desenharCard)}
                  </SortableContext>
                </div>
              ))
            : (
              <SortableContext
                items={exibidos.map((c) => String(c.id))}
                strategy={verticalListSortingStrategy}
              >
                {exibidos.map(desenharCard)}
              </SortableContext>
            )}

          {truncado && (
            <button
              type="button"
              onClick={() => setMostrarTudo(true)}
              className="w-full rounded-xl border border-dashed border-gray-300 py-2.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-50/50"
            >
              Mostrar {restantes === 1 ? "o restante" : `os ${restantes} restantes`}
              {podeReordenar && " · reordenar exige mostrar todos"}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

interface CardProps {
  chamado: ChamadoGestao;
  ocultarStatus: string | null;
  ocultarCategoria: string | null;
}

const CardArrastavel = (props: CardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(props.chamado.id) });

  return (
    <CardChamado
      {...props}
      refContainer={setNodeRef}
      arrastando={isDragging}
      estilo={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      alca={
        <span
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Reordenar chamado ${props.chamado.id}`}
          className="shrink-0 cursor-grab touch-none pt-0.5 text-gray-300 hover:text-gray-500 active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </span>
      }
    />
  );
};

/**
 * Três níveis de leitura: assunto, descrição e uma linha de apoio. A data exata
 * sai da linha e vira título do "aberto há": numa lista, o que faz agir é há
 * quanto tempo aquilo está parado, não o carimbo do dia.
 */
const CardChamado = ({
  chamado,
  ocultarStatus,
  ocultarCategoria,
  alca,
  refContainer,
  estilo,
  arrastando,
}: CardProps & {
  alca?: React.ReactNode;
  refContainer?: (elemento: HTMLElement | null) => void;
  estilo?: CSSProperties;
  arrastando?: boolean;
}) => {
  const navegar = useNavigate();
  const espera = diasDeEspera(chamado);
  const idade = diasDesde(chamado.dataCriacao);
  const descricao = descricaoUtil(chamado);

  return (
    <div
      ref={refContainer}
      style={estilo}
      onClick={() => navegar(`/chamados/${chamado.id}`)}
      className={[
        "flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 border-l-[3px] bg-white px-4 py-3 shadow-sm transition-all hover:border-gray-300 hover:shadow-md",
        bordaSeveridade(chamado.severidade ?? 3),
        arrastando ? "shadow-lg" : "",
      ].join(" ")}
    >
      {alca}

      <span className="w-14 shrink-0 pt-0.5 text-xs font-semibold tabular-nums text-gray-500">
        #{chamado.id}
      </span>

      <div className="min-w-0 flex-1">
        <Link
          to={`/chamados/${chamado.id}`}
          onClick={(e) => e.stopPropagation()}
          className="block truncate font-semibold text-gray-900 hover:text-blue-700 hover:underline"
        >
          {chamado.assunto}
        </Link>

        {descricao && (
          <p className="mt-0.5 truncate text-sm text-gray-600">{descricao}</p>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          {chamado.categoriaChamado &&
            chamado.categoriaChamado !== ocultarCategoria && (
              <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">
                {chamado.categoriaChamado}
              </span>
            )}

          {/* A espera do cliente é o dado mais forte da linha e toma o lugar da idade. */}
          {espera !== null ? (
            <span className="font-semibold text-amber-700">
              aguarda você {textoDeDias(espera)}
            </span>
          ) : (
            idade !== null && (
              <span title={formatarData(chamado.dataCriacao)}>
                aberto {textoDeDias(idade)}
              </span>
            )
          )}

          {chamado.quantidadeInteracoes > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-300">·</span>
              <MessageSquare className="h-3 w-3" />
              {chamado.quantidadeInteracoes}
            </span>
          )}

          {chamado.possuiAnexo && (
            <span className="inline-flex items-center gap-1">
              <span className="text-gray-300">·</span>
              <Paperclip className="h-3 w-3" />
              anexo
            </span>
          )}
        </div>
      </div>

      {/* O status só é carimbado quando diverge do resto da lista. */}
      {chamado.statusChamado && chamado.statusChamado !== ocultarStatus && (
        <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700">
          {chamado.statusChamado}
        </span>
      )}
    </div>
  );
};

export default QuadroSeveridades;
